import React, {useRef, useCallback, useEffect, useState} from 'react';
import Topbar from "../layout/Topbar";
import Sidebar from "../layout/Sidebar";
import {connect} from "react-redux";
import {compose} from "redux";
import DataGrid, {SelectColumn} from 'react-data-grid';
import Spinner from "../spinner/Spinner";
import CustomSnackbar from "../alert/CustomSnackbar";
import {debounce} from "lodash";
import {CurrencyFormatter, IsAtBottom} from "../reporting-table/ReactDataGridUtils";
import {getAccountBaseCurrencySymbol} from "../utils/Filters";
import {workspaceActions} from "../../store/actions/workspaceAction";
import {Box, CssBaseline, Divider, useTheme} from "@mui/material";
import {CustomMainRootBox} from "../dashboard/Dashboard";
import 'react-data-grid/lib/styles.css';
import useCustomStyles from "../utils/UseCustomStyles";
import {CustomTableContainer} from "./LogClicksTable";
import {logConversionsActions} from "../../store/actions/logConversionsAction";
import LogConversionsToolbar from "./LogConversionsToolbar";

import LogEmptyState from "./LogEmptyState";

const customStyles = (theme) => ({
    dataGrid: theme.clicksReportDataGrid
});

function getColumns() {
    let cachedColumns = JSON.parse(localStorage.getItem("log-conversions-columns"));
    let currencySymbol = getAccountBaseCurrencySymbol();
    let columns = [
        SelectColumn,
        {key: 'clickId', name: 'Click ID', hidden: false, width: 200, frozen: true, sortable: true, resizable: true},
        {
            key: 'externalId',
            name: 'External ID',
            hidden: false,
            width: 150,
            sortable: true,
            draggable: true,
            resizable: true
        },
        {
            key: 'transactionId',
            name: 'Transaction ID',
            hidden: false,
            width: 150,
            sortable: true,
            draggable: true,
            resizable: true
        },
        {
            key: 'status',
            name: 'Postback Status',
            hidden: false,
            width: 150,
            sortable: true,
            draggable: true,
            resizable: true
        },
        {
            key: 'type',
            name: 'Conversion Type',
            hidden: false,
            width: 150,
            sortable: true,
            draggable: true,
            resizable: true
        },
        {
            key: 'customConversionEvent',
            name: 'Custom Conversion Event',
            hidden: false,
            width: 200,
            sortable: true,
            draggable: true,
            resizable: true
        },
        {
            key: 'customConversionRevenue', name: 'Custom Conversion Revenue', hidden: false, width: 200, sortable: true, draggable: true, resizable: true,
            renderCell({row}) {
                return CurrencyFormatter(row?.customConversionRevenue, currencySymbol, 4);
            }
        },
        {
            key: 'campaignName',
            name: 'Campaign Name',
            hidden: false,
            width: 150,
            sortable: true,
            draggable: true,
            resizable: true
        },
        {
            key: 'landingName',
            name: 'Landing Name',
            hidden: false,
            width: 150,
            sortable: true,
            draggable: true,
            resizable: true
        },
        {
            key: 'offerName',
            name: 'Offer Name',
            hidden: false,
            width: 150,
            sortable: true,
            draggable: true,
            resizable: true
        },
        {
            key: 'clickedAt',
            name: 'Click Time',
            hidden: false,
            width: 180,
            sortable: true,
            draggable: true,
            resizable: true
        },
        {
            key: 'conversionAt',
            name: 'Conversion Time',
            hidden: false,
            width: 180,
            sortable: true,
            draggable: true,
            resizable: true
        },
        {
            key: 'payout', name: 'Payout', hidden: false, width: 150, sortable: true, draggable: true, resizable: true,
            renderCell({row}) {
                return CurrencyFormatter(row?.payout, currencySymbol, 4);
            }
        },
        {
            key: 'cost', name: 'Cost', hidden: false, width: 150, sortable: true, draggable: true, resizable: true,
            renderCell({row}) {
                return CurrencyFormatter(row?.cost, currencySymbol, 6);
            }
        },
        {
            key: 'deviceVendor',
            name: 'Device Vendor',
            hidden: false,
            width: 150,
            sortable: true,
            draggable: true,
            resizable: true
        },
        {
            key: 'deviceModel',
            name: 'Device Model',
            hidden: false,
            width: 150,
            sortable: true,
            draggable: true,
            resizable: true
        },
        {
            key: 'deviceType',
            name: 'Device Type',
            hidden: false,
            width: 150,
            sortable: true,
            draggable: true,
            resizable: true
        },
        {key: 'os', name: 'OS', hidden: false, width: 150, sortable: true, draggable: true, resizable: true},
        {
            key: 'osVersion',
            name: 'OS Version',
            hidden: false,
            width: 150,
            sortable: true,
            draggable: true,
            resizable: true
        },
        {key: 'browser', name: 'Browser', hidden: false, width: 150, sortable: true, draggable: true, resizable: true},
        {
            key: 'browserVersion',
            name: 'Browser Version',
            hidden: false,
            width: 150,
            sortable: true,
            draggable: true,
            resizable: true
        },
        {key: 'isp', name: 'ISP', hidden: false, width: 150, sortable: true, draggable: true},
        {key: 'country', name: 'Country', hidden: false, width: 150, sortable: true, draggable: true},
        {key: 'state', name: 'State', hidden: false, width: 150, sortable: true, draggable: true},
        {key: 'city', name: 'City', hidden: false, width: 150, sortable: true, draggable: true},
        {key: 'language', name: 'Language', hidden: false, width: 150, sortable: true, draggable: true},
        {key: 'ip', name: 'IP', hidden: false, width: 150, sortable: true, draggable: true},
        {key: 'referrerDomain', name: 'Referrer Domain', hidden: false, width: 150, sortable: true, draggable: true},
        {key: 'custom1', name: 'Custom 1', hidden: false, width: 150, sortable: true, draggable: true},
        {key: 'custom2', name: 'Custom 2', hidden: false, width: 150, sortable: true, draggable: true},
        {key: 'custom3', name: 'Custom 3', hidden: false, width: 150, sortable: true, draggable: true},
        {key: 'custom4', name: 'Custom 4', hidden: false, width: 150, sortable: true, draggable: true},
        {key: 'custom5', name: 'Custom 5', hidden: false, width: 150, sortable: true, draggable: true},
        {key: 'custom6', name: 'Custom 6', hidden: false, width: 150, sortable: true, draggable: true},
        {key: 'custom7', name: 'Custom 7', hidden: false, width: 150, sortable: true, draggable: true},
        {key: 'custom8', name: 'Custom 8', hidden: false, width: 150, sortable: true, draggable: true},
        {key: 'custom9', name: 'Custom 9', hidden: false, width: 150, sortable: true, draggable: true},
        {key: 'custom10', name: 'Custom 10', hidden: false, width: 150, sortable: true, draggable: true},
        {key: 'postbackParam1', name: 'Postback Param 1', hidden: false, width: 150, sortable: true, draggable: true},
        {key: 'postbackParam2', name: 'Postback Param 2', hidden: false, width: 150, sortable: true, draggable: true},
        {key: 'postbackParam3', name: 'Postback Param 3', hidden: false, width: 150, sortable: true, draggable: true},
        {key: 'postbackParam4', name: 'Postback Param 4', hidden: false, width: 150, sortable: true, draggable: true},
        {key: 'postbackParam5', name: 'Postback Param 5', hidden: false, width: 150, sortable: true, draggable: true},
    ];

    if (cachedColumns !== null && cachedColumns?.length !== columns.length) {
        return columns;
    }

    if (cachedColumns !== null && cachedColumns !== "" && cachedColumns !== undefined) {
        let sortedColumns = [];
        cachedColumns?.forEach(cachedCol => {
            columns?.forEach(defCol => {
                if (cachedCol?.key === defCol?.key) {
                    defCol.width = cachedCol.width;
                    sortedColumns.push(defCol);
                }
            });
        });
        return sortedColumns;
    }
    localStorage.setItem("log-conversions-columns", JSON.stringify(columns));
    return columns;
}

function LogConversionsTable(props) {
    const theme = useTheme();
    const classes = useCustomStyles(customStyles, theme);

    const {
        alert, getClicksReport, updateSortBy, updateOrderBy, updateSortColumns, loadMoreRows, history,
        location, resetUpdatedWorkspaceValue
    } = props;
    const {data, isLoading, errorMsg, clickId, orderBy, sortBy, sortColumns, rerender} = props?.logConversionsState;
    const {from, to} = props?.ddToolbarState;
    const {timezone, currencyCode, loaded} = props?.auth?.userSettings;
    const workspaceIds = props.workspace?.userWorkspaces?.filter(item => item.selected && item.workspaceId)
        .map(item => item.workspaceId).join(',') || '';
    const workspaceReady = loaded && workspaceIds !== '' && !props.workspace?.loading?.getUserWorkspaces;

    const gridRef = useRef(null);

    const [selectedRows, setSelectedRows] = useState(() => new Set());
    const [columns, setColumns] = useState(getColumns());

    const onColumnsReorder = (sourceKey, targetKey) => {
        const sourceColumnIndex = columns.findIndex((c) => c.key === sourceKey);
        const targetColumnIndex = columns.findIndex((c) => c.key === targetKey);
        const reorderedColumns = [...columns];
        reorderedColumns.splice(targetColumnIndex, 0, reorderedColumns.splice(sourceColumnIndex, 1)[0]);
        localStorage.setItem("log-conversions-columns", JSON.stringify(reorderedColumns));
        setColumns(reorderedColumns);
    };

    const handleInfinityScroll = (event) => {
        if (!workspaceReady || isLoading || !IsAtBottom(event)) {
            return;
        }

        let disable = data.page >= Math.ceil(data?.totalRecords / data?.recordsPerPage);
        if (!disable) {
            loadMoreRows(from, to, timezone, clickId, data.page + 1, 100, orderBy, sortBy, workspaceIds);
        }
    };

    const handleGetClicksReport = useCallback(() => {
        if (!workspaceReady) return;
        gridRef.current?.element?.scrollTo(0, 0);
        getClicksReport(from, to, timezone, clickId, 1, 100, orderBy, sortBy, workspaceIds);
    }, [workspaceReady, workspaceIds, getClicksReport, from, to, timezone, clickId, orderBy, sortBy]);

    const handleRefresh = () => {
        gridRef?.current?.element?.scrollTo(0, 0);
        handleGetClicksReport();
    };

    const cacheColumnResize = (idx, width) => {
        let cachedColumns = JSON.parse(localStorage.getItem("log-conversions-columns"));
        let modColumns = [];
        cachedColumns?.forEach((item, index) => {
            if (index === idx) {
                item.width = width;
            }
            modColumns.push(item);
        });
        localStorage.setItem("log-conversions-columns", JSON.stringify(modColumns));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleColumnResize = useCallback(debounce(cacheColumnResize, 50), []);

    useEffect(() => {
        handleGetClicksReport();
    }, [handleGetClicksReport, currencyCode]);

    // Only for rerender
    useEffect(() => {
        if (rerender) {
            handleGetClicksReport();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rerender]);

    useEffect(() => {
        if (sortColumns.length === 0) {
            updateSortBy("");
            updateOrderBy("");
            return () => {
            }
        }

        const {columnKey, direction} = sortColumns[0];
        updateOrderBy(columnKey);
        updateSortBy(direction);

    }, [updateOrderBy, updateSortBy, sortColumns]);

    // Selection changes are fetched by the report effect above.
    useEffect(() => {
        if (props.workspace?.updated != null) resetUpdatedWorkspaceValue();
    }, [props.workspace?.updated, resetUpdatedWorkspaceValue]);

    return (
        <CustomMainRootBox>
            <CssBaseline/>
            <Sidebar {...props}/>
            <Topbar/>
            <CustomTableContainer>
                <LogConversionsToolbar
                    rows={data?.records ? data.records : []}
                    onClickRefresh={handleRefresh}
                    searchTerm={clickId}
                    onChangeSearch={(searchTerm) => {
                        props.updateClickId(searchTerm);
                    }}
                    selectedRows={selectedRows}
                    history={history}
                    location={location}
                />
                <Divider/>
                <Box sx={{flex: 1, minHeight: 0, minWidth: 0, width: "100%", position: 'relative'}}>

                    {alert.errorMsg !== "" && <CustomSnackbar message={alert.errorMsg}/>}
                    {(isLoading || !loaded || props.workspace?.userWorkspaces == null || props.workspace?.loading?.getUserWorkspaces) && <Spinner overComponentBox={true}/>}

                    {(columns !== null && classes !== null) &&
                        <DataGrid
                            ref={gridRef}
                            rowKeyGetter={(row) => row.uniqueId}
                            columns={columns}
                            onColumnsReorder={onColumnsReorder}
                            rows={data?.records ? data.records : []}
                            defaultColumnOptions={{
                                sortable: true,
                                resizable: true,
                                draggable: true
                            }}
                            onColumnResize={(idx, width) => handleColumnResize(idx, width)}
                            selectedRows={selectedRows}
                            onSelectedRowsChange={setSelectedRows}
                            onSelectedCellChange={({rowIdx, row, column}) => {
                                if (rowIdx !== -1 && column?.key !== "select-row") {
                                    setSelectedRows(new Set().add(row.uniqueId));
                                }
                            }}
                            sortColumns={sortColumns}
                            onSortColumnsChange={updateSortColumns}
                            onScroll={handleInfinityScroll}
                            enableVirtualization={true}
                            className={classes?.dataGrid}
                        />
                    }
                    {workspaceReady && !isLoading && !errorMsg && columns !== null && classes !== null &&
                        data?.page > 0 && !data?.records?.length && <LogEmptyState kind="conversions"/>}
                </Box>
            </CustomTableContainer>
        </CustomMainRootBox>
    );
}

const mapStateToProps = (state) => {
    return {
        logConversionsState: state.logConversions,
        ddToolbarState: state.ddToolbar,
        auth: state.auth,
        alert: state.alert,
        workspace: state.workspace
    }
};

const actionCreators = {
    getClicksReport: logConversionsActions.getConversions,
    loadMoreRows: logConversionsActions.loadMoreConversionRows,
    updateOrderBy: logConversionsActions.updateOrderBy,
    updateSortBy: logConversionsActions.updateSortBy,
    updateClickId: logConversionsActions.updateClickId,
    updateSortColumns: logConversionsActions.updateSortColumns,
    resetUpdatedWorkspaceValue: workspaceActions.resetUpdatedWorkspaceValue
};

export default compose(connect(mapStateToProps, actionCreators))(LogConversionsTable);
